import { isPlatformBrowser } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject, signal } from '@angular/core';
import { ActivationEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import Lenis from 'lenis';
import { firstValueFrom } from 'rxjs';
import { langLink, stripLangPath } from '../services/i18n-live';
import { ScrollTrigger, gsap } from './gsap';
import { MOTION_CONFIG } from './motion.config';
import { QUERIES, mq, queryFlag } from './motion-env';

export interface ScrollToOptions {
  offset?: number;
  immediate?: boolean;
  duration?: number;
}

type NavigationTrigger = NavigationStart['navigationTrigger'];

const LANG_ATTR: Record<string, string> = { am: 'hy', en: 'en', ru: 'ru' };
const SCROLLED_THRESHOLD = 100;

/**
 * Owns the site-wide motion runtime: reduced-motion and pointer detection, Lenis smooth
 * scrolling synced to ScrollTrigger, scroll handling on route changes, language-aware
 * section scrolling and debounced ScrollTrigger refreshes. Everything runs outside the
 * Angular zone so scroll and animation frames never trigger change detection.
 */
@Injectable({ providedIn: 'root' })
export class MotionService {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly reducedState = signal(this.isBrowser && mq(QUERIES.reduce));
  private readonly fineState = signal(this.isBrowser && mq(QUERIES.fine));
  private readonly scrolledState = signal(this.isBrowser && window.scrollY > SCROLLED_THRESHOLD);

  /** Live `prefers-reduced-motion: reduce`. */
  readonly reduced = this.reducedState.asReadonly();
  /** Live `(hover: hover) and (pointer: fine)`. */
  readonly finePointer = this.fineState.asReadonly();
  /** `scrollY > 100`, updated in the zone only when it flips. */
  readonly scrolled = this.scrolledState.asReadonly();

  /** Resolves when web fonts are ready, or after 600 ms at most. */
  readonly fontsReady: Promise<void> = this.isBrowser
    ? Promise.race([
        document.fonts?.ready.then(() => undefined) ?? Promise.resolve(),
        new Promise<void>((resolve) => setTimeout(resolve, 600)),
      ])
    : Promise.resolve();

  private initialized = false;
  private lenis: Lenis | null = null;
  private tick: ((time: number) => void) | null = null;
  private velocityST: ScrollTrigger | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshAfterScroll: (() => void) | null = null;
  private langSwitchAt = -Infinity;
  private readonly counts: Record<string, number> = {};

  private navigationTrigger: NavigationTrigger = 'imperative';
  private pathChanged = false;
  private firstActivation = false;
  private firstNavigationDone = false;
  private pendingSectionScroll: (() => void) | null = null;

  constructor() {
    if (!this.isBrowser) return;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) this.onNavigationStart(event);
      else if (event instanceof ActivationEnd) this.onActivationEnd();
      else if (event instanceof NavigationEnd) this.onNavigationEnd(event);
    });
  }

  /** Idempotent, browser-only. Called once by AppComponent after the first render. */
  init(): void {
    if (!this.isBrowser || this.initialized) return;
    this.initialized = true;

    this.outside(() => {
      const root = document.documentElement;

      const reduceQuery = window.matchMedia(QUERIES.reduce);
      const fineQuery = window.matchMedia(QUERIES.fine);
      const syncMedia = () => {
        this.reducedState.set(reduceQuery.matches);
        this.fineState.set(fineQuery.matches);
        root.classList.toggle('motion-ok', !reduceQuery.matches);
        root.classList.toggle('fine-pointer', fineQuery.matches);
        this.updateLenis();
      };
      reduceQuery.addEventListener('change', syncMedia);
      fineQuery.addEventListener('change', syncMedia);
      syncMedia();

      let scrollFrame = 0;
      window.addEventListener(
        'scroll',
        () => {
          if (scrollFrame) return;
          scrollFrame = requestAnimationFrame(() => {
            scrollFrame = 0;
            const scrolled = window.scrollY > SCROLLED_THRESHOLD;
            if (scrolled !== this.scrolledState()) this.zone.run(() => this.scrolledState.set(scrolled));
          });
        },
        { passive: true }
      );

      this.velocityST = ScrollTrigger.create({ start: 0, end: 'max' });

      // A media-query flip (rotation, resize across a breakpoint, reduced motion toggled) reverts
      // and re-runs gsap.matchMedia contexts. ScrollTriggers created synchronously in those
      // callbacks refresh on creation and wipe the scroll position ScrollTrigger recorded, so its
      // final refresh would leave the page at the top. Record it first, restore it last.
      let scrollBeforeMedia = -1;
      (gsap as unknown as { addEventListener(type: string, fn: () => void): void }).addEventListener(
        'matchMediaInit',
        () => (scrollBeforeMedia = window.scrollY)
      );
      ScrollTrigger.addEventListener('matchMedia', () => {
        const top = scrollBeforeMedia;
        scrollBeforeMedia = -1;
        if (top < 0 || Math.abs(window.scrollY - top) <= 1) return;
        if (this.lenis) this.lenis.scrollTo(top, { immediate: true, force: true });
        else window.scrollTo({ top, left: 0, behavior: 'instant' });
        ScrollTrigger.update();
      });

      this.fontsReady.then(() => this.requestRefresh());
      window.addEventListener('load', () => this.requestRefresh(), { once: true });
      // Late images (capture phase: `load` does not bubble) change layout below them.
      // Map tiles are fixed-size and keep loading, so they are ignored.
      document.addEventListener(
        'load',
        (event) => {
          const target = event.target as Element | null;
          if (target?.tagName === 'IMG' && !target.closest('google-map, .map')) this.requestRefresh();
        },
        true
      );

      this.translate.onLangChange.subscribe((event) => {
        root.lang = LANG_ATTR[event.lang] ?? event.lang;
        this.afterFrames(2, () => this.requestRefresh());
      });
      if (this.translate.currentLang) root.lang = LANG_ATTR[this.translate.currentLang] ?? this.translate.currentLang;

      if (queryFlag('motiondebug')) {
        (window as unknown as { __motion: unknown }).__motion = {
          triggers: () => ScrollTrigger.getAll().length,
          lenis: () => !!this.lenis,
          counts: this.counts,
        };
      }
    });
  }

  outside<T>(fn: () => T): T {
    return this.zone.runOutsideAngular(fn);
  }

  /** Runs `fn` outside the zone after the next paint: for setup nothing on screen is waiting for. */
  afterPaint(fn: () => void): void {
    if (!this.isBrowser) return;
    this.outside(() => requestAnimationFrame(() => setTimeout(fn)));
  }

  /**
   * Runs `fn` now if `el` is in the first viewport (a hidden from-state must be applied before
   * the first paint), otherwise after the next paint, so below-the-fold animation setup does not
   * hold up the first paint and the hero image download.
   */
  whenNeeded(el: Element, fn: () => void): void {
    if (!this.isBrowser) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight && rect.width + rect.height > 0) this.outside(fn);
    else this.afterPaint(fn);
  }

  /**
   * Debounced `ScrollTrigger.sort()` + `refresh()`, for layout changes after async content.
   * A refresh re-applies the scroll position, which would cancel a smooth scroll in progress
   * (native or Lenis), so it waits until scrolling has stopped.
   */
  requestRefresh(): void {
    if (!this.isBrowser) return;
    this.outside(() => {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        if (ScrollTrigger.isScrolling() || this.lenis?.isScrolling) {
          if (this.refreshAfterScroll) return;
          this.refreshAfterScroll = () => {
            ScrollTrigger.removeEventListener('scrollEnd', this.refreshAfterScroll!);
            this.refreshAfterScroll = null;
            this.requestRefresh();
          };
          ScrollTrigger.addEventListener('scrollEnd', this.refreshAfterScroll);
          return;
        }
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
      }, MOTION_CONFIG.refreshDebounceMs);
    });
  }

  /** Current scroll velocity in px/s (0 before init). */
  scrollVelocity(): number {
    return this.velocityST?.getVelocity() ?? 0;
  }

  scrollTo(target: number | string | HTMLElement, opts: ScrollToOptions = {}): Promise<void> {
    if (!this.isBrowser) return Promise.resolve();
    const element =
      typeof target === 'string' ? document.querySelector<HTMLElement>(target) : typeof target === 'number' ? null : target;
    if (typeof target !== 'number' && !element) return Promise.resolve();

    const immediate = !!opts.immediate || this.reduced();
    const offset = opts.offset ?? (element ? -MOTION_CONFIG.navHeight : 0);

    return this.outside(
      () =>
        new Promise<void>((resolve) => {
          if (this.lenis) {
            // Lenis caches the scroll limit (refreshed by a debounced ResizeObserver), so it can
            // still hold the previous page's height right after a route change. It would also
            // subtract the target's scroll-margin-top on top of our offset, so pass a pixel value.
            this.lenis.resize();
            const top = element ? element.getBoundingClientRect().top + this.lenis.animatedScroll + offset : (target as number);
            // onComplete never fires when the scroll is interrupted (user input, a reset), so settle
            // the promise after the scroll's duration at the latest.
            const fallback = setTimeout(resolve, ((opts.duration ?? 1.1) + 0.4) * 1000);
            this.lenis.scrollTo(top, {
              immediate,
              duration: opts.duration ?? 1.1,
              easing: (t: number) => 1 - Math.pow(1 - t, 4),
              force: true,
              onComplete: () => {
                clearTimeout(fallback);
                resolve();
              },
            });
            if (immediate) {
              clearTimeout(fallback);
              resolve();
            }
            return;
          }

          const top = element ? element.getBoundingClientRect().top + window.scrollY + offset : (target as number);
          if (immediate) {
            window.scrollTo({ top, left: 0, behavior: 'instant' });
            resolve();
            return;
          }
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            window.removeEventListener('scrollend', finish);
            resolve();
          };
          window.addEventListener('scrollend', finish, { once: true });
          setTimeout(finish, 1500);
          window.scrollTo({ top, left: 0, behavior: 'smooth' });
        })
    );
  }

  /** Scrolls to a home-page section, navigating to the (language-aware) home page first if needed. */
  scrollToSection(id: string, event?: Event): Promise<void> {
    event?.preventDefault();
    if (!this.isBrowser) return Promise.resolve();
    const scrolled = this.isHome()
      ? this.scrollTo('#' + id)
      : new Promise<void>((resolve) => {
          this.pendingSectionScroll?.();
          this.pendingSectionScroll = resolve;
          this.zone.run(() => this.router.navigate([this.homePath()], { fragment: id })).then((ok) => {
            if (!ok && this.pendingSectionScroll === resolve) {
              this.pendingSectionScroll = null;
              resolve();
            }
          });
        });
    return scrolled.then(() => this.focusSection(id));
  }

  /** '/#about' | '/en#about' | '/ru#about' */
  sectionHref(id: string): string {
    return `${this.homePath()}#${id}`;
  }

  /** '/' | '/en' | '/ru' */
  homePath(): string {
    return langLink(this.translate, '/');
  }

  isHome(): boolean {
    return stripLangPath(this.router.url) === '/';
  }

  /** Marks the next navigation as a language-only switch: no route slide, no replays. */
  beginLanguageSwitch(): void {
    if (!this.isBrowser) return;
    document.documentElement.dataset['vt'] = 'instant';
    this.langSwitchAt = performance.now();
  }

  /** True only within `withinMs` after beginLanguageSwitch(). */
  recentLanguageSwitch(withinMs: number = MOTION_CONFIG.langSwitchWindowMs): boolean {
    return this.isBrowser && performance.now() - this.langSwitchAt < withinMs;
  }

  lockScroll(on: boolean): void {
    if (on) this.lenis?.stop();
    else this.lenis?.start();
  }

  /** Debug counters, exposed as `__motion.counts` with `?motiondebug=1`. */
  track(counter: string, delta: number): void {
    this.counts[counter] = (this.counts[counter] ?? 0) + delta;
  }

  /** Moves keyboard focus to the section, so the next Tab continues from there (like an anchor jump). */
  private focusSection(id: string): void {
    const section = document.getElementById(id);
    if (!section) return;
    if (!section.hasAttribute('tabindex')) section.setAttribute('tabindex', '-1');
    section.focus({ preventScroll: true });
  }

  private onNavigationStart(event: NavigationStart): void {
    this.navigationTrigger = event.navigationTrigger;
    this.pathChanged = stripLangPath(event.url) !== stripLangPath(this.router.url);
    this.firstActivation = true;
  }

  /**
   * Runs inside the View Transition update callback, so jumping to the top here is hidden
   * under the old page snapshot. A language-only switch keeps its scroll position.
   */
  private onActivationEnd(): void {
    if (!this.firstActivation) return;
    this.firstActivation = false;
    if (!this.pathChanged || this.navigationTrigger === 'popstate') return;
    this.outside(() => {
      if (this.lenis) this.lenis.scrollTo(0, { immediate: true, force: true });
      else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const isInitial = !this.firstNavigationDone;
    this.firstNavigationDone = true;

    this.outside(() => {
      this.updateLenis();
      const root = document.documentElement;
      if (isInitial) requestAnimationFrame(() => root.classList.add('vt-ready'));
      if (root.dataset['vt']) setTimeout(() => delete root.dataset['vt'], 400);

      // Safety net: triggers whose element left the DOM without being reverted.
      ScrollTrigger.getAll()
        .filter((st) => st.trigger && !(st.trigger as Element).isConnected)
        .forEach((st) => {
          if (queryFlag('motiondebug')) console.warn('[motion] killed orphaned ScrollTrigger', st.trigger);
          st.kill();
        });

      this.afterFrames(2, () => this.requestRefresh());

      const fragment = this.router.parseUrl(event.urlAfterRedirects).fragment;
      const pending = this.pendingSectionScroll;
      this.pendingSectionScroll = null;
      if (fragment) {
        this.whenContentReady()
          .then(() => this.scrollTo('#' + fragment, { immediate: isInitial }))
          .then(() => pending?.());
      } else {
        pending?.();
      }
    });
  }

  private updateLenis(): void {
    if (!this.isBrowser || !this.initialized) return;
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    const want =
      MOTION_CONFIG.smoothScroll && this.fineState() && !this.reducedState() && route.data['smoothScroll'] !== false;

    if (want && !this.lenis) {
      this.outside(() => {
        const lenis = new Lenis({
          autoRaf: false,
          lerp: 0.12,
          smoothWheel: true,
          syncTouch: false,
          anchors: false,
          allowNestedScroll: true,
          // Not stopInertiaOnNavigate: its click handler resets Lenis whenever a clicked link's
          // pathname differs from the current one, even after preventDefault(), which cancelled
          // section scrolls started from /home, /am or a stored-language home page. Route changes
          // stop inertia anyway: onActivationEnd jumps to the top with `force`.
          prevent: (node: HTMLElement) => !!node.closest('.map, .lead-form, app-modal, [data-lenis-prevent]'),
        });
        lenis.on('scroll', ScrollTrigger.update);
        this.tick = (time: number) => lenis.raf(time * 1000);
        gsap.ticker.add(this.tick);
        gsap.ticker.lagSmoothing(0);
        this.lenis = lenis;
      });
    } else if (!want && this.lenis) {
      if (this.tick) gsap.ticker.remove(this.tick);
      this.tick = null;
      gsap.ticker.lagSmoothing(500, 33);
      this.lenis.destroy();
      this.lenis = null;
    }
  }

  /** Fonts and the current translations are in, then layout has settled for two frames. */
  private whenContentReady(): Promise<void> {
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 1500));
    return Promise.race([Promise.all([this.fontsReady, this.translationsLoaded()]).then(() => undefined), timeout])
      .then(() => new Promise<void>((resolve) => this.afterFrames(2, resolve)))
      .then(() => ScrollTrigger.refresh());
  }

  private translationsLoaded(): Promise<void> {
    const lang = this.translate.currentLang;
    if (lang && this.translate.translations[lang]) return Promise.resolve();
    return firstValueFrom(this.translate.onLangChange).then(() => undefined);
  }

  private afterFrames(count: number, fn: () => void): void {
    if (count <= 0) {
      fn();
      return;
    }
    requestAnimationFrame(() => this.afterFrames(count - 1, fn));
  }
}
